/**
 * CarMotionEngine — pure GPS-fix → displayed-pose motion model for the
 * Experimental Premium Live Map Layer (PLA-271, ADR-0030).
 *
 * Turns the sparse raw tracking fixes (every ~15 s / 50 m, heading and speed
 * stripped from the customer payload) into a continuously *driving* display
 * pose instead of the legacy stop-and-go glide:
 *
 * - Each new fix starts a segment FROM THE CURRENT DISPLAYED POSE to the fix,
 *   over the real fix-timestamp gap × ~1.2 stretch (clamped ~1–20 s). The
 *   stretch means the next fix usually arrives mid-segment, so retargeting
 *   keeps the car rolling instead of stalling — the display deliberately lags
 *   reality by roughly one fix interval (do not "fix" that, see ADR-0030).
 * - The path is a Catmull-Rom-style Hermite curve: it leaves the current pose
 *   along the currently displayed heading and arrives at the fix along the
 *   raw direction of travel, so the heading rotates continuously through
 *   corners. With fewer than 3 known fixes it falls back to the straight chord.
 * - Velocity is linear mid-drive (constant ground speed via arc-length
 *   reparameterization). Ease-in applies only when pulling away from a
 *   standstill; the short ease-out tail is only ever *reached* when no
 *   successor fix arrived (a mid-segment fix retargets before the tail
 *   plays), i.e. at genuine arrival/rest — no per-segment ease-out "pulsing".
 * - Plausibility-capped catch-up: a fix implying > ~140 km/h or > ~500 m from
 *   the displayed pose compresses into a fast ~1.5 s straight glide.
 * - Staleness flag after 90 s without a fix (platform-wide staleness window);
 *   clears instantly on the next fix. Display-only — consumers add visuals.
 * - Reduced motion: the pose snaps per fix, consistent with the follow
 *   camera's jump cut. The flag is read from config on every call so live
 *   preference flips take effect immediately.
 *
 * Heading convention matches car-sprite-engine.ts: compass degrees,
 * 0° = north, increasing clockwise. Internal geometry lives in
 * ./car-motion-geometry (split for the 500-line module cap).
 *
 * This module is intentionally pure TypeScript: no DOM, no google.maps, no
 * React — and it NEVER reads the clock. Callers pass `nowMs` into both
 * `pushFix` and `resolvePose` from one consistent time source; fix
 * `timestampMs` values may live on a different (server) clock and are only
 * ever used for gaps, with an arrival-clock fallback when unreliable.
 */

import {
  bearingBetween,
  buildSegment,
  clamp,
  displayNow,
  distanceMeters,
  MotionSegment,
} from "./car-motion-geometry"

/** Tunable behavior for the motion engine. */
export interface CarMotionConfig {
  /** Segment duration = fix gap × this stretch (ADR-0030: ~1.2). */
  durationStretch: number
  /** Lower clamp for the stretched segment duration. */
  minSegmentDurationMs: number
  /** Upper clamp for the stretched segment duration. */
  maxSegmentDurationMs: number
  /** Catch-up triggers above this implied speed (~39 m/s ≈ 140 km/h). */
  maxPlausibleSpeedMps: number
  /** Catch-up triggers above this distance from the displayed pose. */
  maxPlausibleDistanceMeters: number
  /** Duration of the compressed catch-up glide. */
  catchUpDurationMs: number
  /** GPS silence (arrival clock) after which `isStale` is reported. */
  staleAfterMs: number
  /** Ease ramp length; capped at 40% of the duration per side. */
  easeRampMs: number
  /** When true the pose snaps to the latest fix — no interpolation. */
  reducedMotion: boolean
}

export const DEFAULT_CAR_MOTION_CONFIG: CarMotionConfig = {
  durationStretch: 1.2,
  minSegmentDurationMs: 1000,
  maxSegmentDurationMs: 20000,
  maxPlausibleSpeedMps: 39,
  maxPlausibleDistanceMeters: 500,
  catchUpDurationMs: 1500,
  staleAfterMs: 90000,
  easeRampMs: 450,
  reducedMotion: false,
}

/** A raw GPS fix as delivered by the tracking pipeline. */
export interface CarMotionFix {
  lat: number
  lng: number
  /** Fix timestamp (possibly server epoch ms; used only for gaps). */
  timestampMs: number
}

/** Per-animation-frame input. `nowMs` must share the clock of `pushFix`. */
export interface CarMotionTick {
  nowMs: number
}

/** What the map should render this frame. */
export interface DisplayPose {
  lat: number
  lng: number
  /** Tangent direction of motion (compass deg); held while at rest. */
  headingDeg: number
  /** Displayed ground speed (0 at rest). */
  speedMps: number
  /** True once the last fix is older than `staleAfterMs` (arrival clock). */
  isStale: boolean
}

/** Engine state. Treat as opaque; create via `createMotionState`. */
export interface CarMotionState {
  /** Currently displayed position, or null before the first fix. */
  displayLat: number | null
  displayLng: number | null
  /** Last resolved heading; held through rest / undefined geometry. */
  headingDeg: number
  hasHeading: boolean
  segment: MotionSegment | null
  lastFix: CarMotionFix | null
  /** Arrival time (caller clock) of the last fix — staleness baseline. */
  lastFixArrivalMs: number | null
  fixCount: number
}

export interface CarMotionResolution {
  state: CarMotionState
  /** Pose to render, or null before the first fix. */
  pose: DisplayPose | null
  /**
   * True when there is nothing left to animate (no active segment). Callers
   * may idle-stop their frame loop; the next `pushFix` provides the restart.
   */
  isResting: boolean
}

/** Creates the initial engine state (no fix ingested yet). */
export function createMotionState(): CarMotionState {
  return {
    displayLat: null,
    displayLng: null,
    headingDeg: 0,
    hasHeading: false,
    segment: null,
    lastFix: null,
    lastFixArrivalMs: null,
    fixCount: 0,
  }
}

/**
 * Display fields after snapping straight to `fix` (reduced motion). The
 * heading turns toward the fix when the snap moved far enough to define a
 * bearing; otherwise it is held. Shared by `pushFix` and `resolvePose`.
 */
function snappedDisplay(
  displayLat: number,
  displayLng: number,
  heldHeadingDeg: number,
  hasHeading: boolean,
  fix: CarMotionFix,
): Pick<CarMotionState, "displayLat" | "displayLng" | "headingDeg" | "hasHeading" | "segment"> {
  const bearing = bearingBetween(displayLat, displayLng, fix.lat, fix.lng)
  return {
    displayLat: fix.lat,
    displayLng: fix.lng,
    headingDeg: bearing ?? heldHeadingDeg,
    hasHeading: hasHeading || bearing !== null,
    segment: null,
  }
}

/**
 * Ingests a raw fix and (re)targets the display segment.
 *
 * Pure: returns a NEW state; never mutates inputs. `nowMs` is the arrival
 * time on the caller's tick clock (the engine never reads the clock itself).
 *
 * Behavior:
 * - First fix snaps the displayed pose (nothing to animate from).
 * - Segment duration = fix-timestamp gap × `durationStretch`, clamped; falls
 *   back to the arrival-clock gap when timestamps are non-positive/repeated.
 * - Mid-flight retargeting: the new segment starts from the CURRENT displayed
 *   pose — position continuity, never a backward jump or teleport.
 * - Plausibility cap: implausible fixes compress into a fast straight glide.
 * - No stationary jitter gate — every fix animates, however small (the sprite
 *   engine's speed gate already holds the visual heading when slow).
 */
export function pushFix(
  state: CarMotionState,
  fix: CarMotionFix,
  nowMs: number,
  config: Partial<CarMotionConfig> = {},
): CarMotionState {
  const cfg: CarMotionConfig = { ...DEFAULT_CAR_MOTION_CONFIG, ...config }
  // Never ingest invalid geo — the WEB-1Q NaN guard, mirrored at engine level.
  if (!Number.isFinite(fix.lat) || !Number.isFinite(fix.lng)) {
    return state
  }

  // First fix: snap the display; nothing to animate yet.
  if (state.lastFix === null || state.displayLat === null || state.displayLng === null) {
    return {
      ...state,
      displayLat: fix.lat,
      displayLng: fix.lng,
      segment: null,
      lastFix: fix,
      lastFixArrivalMs: nowMs,
      fixCount: state.fixCount + 1,
    }
  }

  // Reduced motion: per-fix snap, consistent with the follow camera jump cut.
  if (cfg.reducedMotion) {
    return {
      ...state,
      ...snappedDisplay(
        state.displayLat,
        state.displayLng,
        state.headingDeg,
        state.hasHeading,
        fix,
      ),
      lastFix: fix,
      lastFixArrivalMs: nowMs,
      fixCount: state.fixCount + 1,
    }
  }

  // Position continuity: the new segment departs from where the car is
  // DISPLAYED right now, not from the previous raw fix (kills the legacy
  // 1000ms-update vs 7000ms-animator teleport).
  const current = displayNow(
    state.segment,
    state.displayLat,
    state.displayLng,
    state.headingDeg,
    nowMs,
  )

  // Segment duration from the fix-timestamp gap (server clock); fall back to
  // the arrival clock when timestamps are unreliable/identical.
  let gapMs = fix.timestampMs - state.lastFix.timestampMs
  if (!Number.isFinite(gapMs) || gapMs <= 0) {
    gapMs = state.lastFixArrivalMs !== null ? Math.max(0, nowMs - state.lastFixArrivalMs) : 0
  }
  let durationMs = clamp(
    gapMs * cfg.durationStretch,
    cfg.minSegmentDurationMs,
    cfg.maxSegmentDurationMs,
  )

  // Plausibility-capped catch-up (ADR-0030 §5): don't pretend to drive an
  // implausible move — compress it into a fast straight glide.
  const chordMeters = distanceMeters(current.lat, current.lng, fix.lat, fix.lng)
  const impliedSpeedMps =
    gapMs > 0 ? chordMeters / (gapMs / 1000) : chordMeters > 0 ? Number.POSITIVE_INFINITY : 0
  const catchUp =
    chordMeters > cfg.maxPlausibleDistanceMeters || impliedSpeedMps > cfg.maxPlausibleSpeedMps
  if (catchUp) {
    durationMs = cfg.catchUpDurationMs
  }

  const segment = buildSegment({
    startMs: nowMs,
    durationMs,
    easeIn: !current.moving, // pulling away from a standstill
    easeRampMs: cfg.easeRampMs,
    startLat: current.lat,
    startLng: current.lng,
    startHeadingDeg: state.hasHeading ? current.headingDeg : null,
    prevFix: state.lastFix,
    fix,
    // Spline needs ≥ 3 known points (this fix is point fixCount + 1).
    allowSpline: !catchUp && state.fixCount >= 2,
  })

  return {
    displayLat: current.lat,
    displayLng: current.lng,
    headingDeg: current.headingDeg,
    hasHeading: state.hasHeading || current.headingResolved,
    segment,
    lastFix: fix,
    lastFixArrivalMs: nowMs,
    fixCount: state.fixCount + 1,
  }
}

/**
 * Resolves the display pose for one animation frame.
 *
 * Pure: returns a NEW state plus the pose; never mutates inputs. Emits
 * `pose: null` until the first fix has been ingested. `isResting` signals
 * that the frame loop may idle-stop (restart on the next `pushFix`).
 */
export function resolvePose(
  state: CarMotionState,
  input: CarMotionTick,
  config: Partial<CarMotionConfig> = {},
): CarMotionResolution {
  const cfg: CarMotionConfig = { ...DEFAULT_CAR_MOTION_CONFIG, ...config }
  if (state.lastFix === null || state.displayLat === null || state.displayLng === null) {
    return { state, pose: null, isResting: true }
  }

  const isStale =
    state.lastFixArrivalMs !== null && input.nowMs - state.lastFixArrivalMs > cfg.staleAfterMs

  // Reduced motion (checked per tick — live preference flips must work):
  // snap to the latest fix and drop any in-flight segment, so a later flip
  // back to full motion never resumes a stale segment backwards.
  if (cfg.reducedMotion) {
    const fix = state.lastFix
    const snapped = snappedDisplay(
      state.displayLat,
      state.displayLng,
      state.headingDeg,
      state.hasHeading,
      fix,
    )
    return {
      state: { ...state, ...snapped },
      pose: { lat: fix.lat, lng: fix.lng, headingDeg: snapped.headingDeg, speedMps: 0, isStale },
      isResting: true,
    }
  }

  const current = displayNow(
    state.segment,
    state.displayLat,
    state.displayLng,
    state.headingDeg,
    input.nowMs,
  )
  const next: CarMotionState = {
    ...state,
    displayLat: current.lat,
    displayLng: current.lng,
    headingDeg: current.headingDeg,
    hasHeading: state.hasHeading || current.headingResolved,
    // A finished segment is consumed — its end pose is the new rest pose.
    segment: current.moving ? state.segment : null,
  }
  return {
    state: next,
    pose: {
      lat: current.lat,
      lng: current.lng,
      headingDeg: current.headingDeg,
      speedMps: current.speedMps,
      isStale,
    },
    isResting: !current.moving,
  }
}
